import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult, MessageTypeClass } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../../framework/appUtils";
import * as store from "../../../../../framework/customStore";
import { SaveDataObj } from "../../../../../framework/saveData";
import * as messageService from "../../../../../models/services/messageService";
import * as storeActions from "../../../../../models/store/storeActions";
import { ISessionState } from "../../../../../models/session/sessionState";
import { Dialog } from "../../../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../../../framework/Dialog/DialogItemsNav";
import { FormField } from "../../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../../../framework/Grid/GridRow";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { IMessageTypeEditItem } from "source/models/messageType/messageTypeEditItem";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface IMessageTypeDialogProp {
    sessionState: ISessionState;
    id: string;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onOk: () => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (ids: string[]) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface IMessageTypeDialogState {
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    name: string;
    file: IFileAttachment;
    image: string;
    defaultImage: string;
    resetImage: boolean;
    messageTypeClass: MessageTypeClass;
       
    item: IMessageTypeEditItem;
}

export class MessageTypeDialog extends React.Component<IMessageTypeDialogProp, IMessageTypeDialogState> {
    private orgStateHash: string = "";

    constructor(props: IMessageTypeDialogProp) {
        super(props);
        this.state = {
            item: null,
            id: "",
            rowId: "",
            isNew: false,
            code: "",
            name: "",
            file: null,
            image: "",
            defaultImage: "",
            resetImage: false,
            messageTypeClass: MessageTypeClass.NormalMessage,
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => messageService.getMessageTypeEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.item;
        const state = {
            ...this.state,
            item,
            id: item.id,
            rowId: item.rowId,
            isNew: item.isNew(),
            code: item.code,
            name: item.name,
            file: null,
            image: item.image,
            defaultImage: item.defaultImage,
            resetImage: !item.image,
            messageTypeClass: item.messageTypeClass
        };
        this.setState(state);
        const saveData = await this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IMessageTypeDialogProp, prevState: IMessageTypeDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "name") {
            this.setState({ name: value });
        }
    };

    handleSetImage = async (file: File) => {
        if (!file) return;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!attachment) return;
        if (!AppUtils.validateFileAttachment(attachment)) return;
        this.setState({ file: attachment, resetImage: false, image: attachment.thumbnail });
    };

    handleRemoveImage = () => {
        this.setState({ file: null, resetImage: true, image: "" });
    };

    getSaveDataFromState = async (props: IMessageTypeDialogProp, state: IMessageTypeDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("name", state.name);
        result.append("resetImage", state.resetImage);
        if (state.file) {
            result.append("attachments", await SaveAttachmentBase64.fromFileAttachments([state.file]));
        }
        result.append("messageTypeClass", state.messageTypeClass);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.MessageTypeCodeMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => messageService.saveMessageTypeEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onOk();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveMessageTypeParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove([state.id]);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut((await this.getSaveDataFromState(props, this.state)).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        return (
            <Dialog
                id="messageTypeDialog"
                maxWidth="sm"
                title={state.isNew ? Translations.AddMessageType : (Translations.MessageType + ": " + state.item.code)}
                body={
                    <Box display="flex" flexDirection="column">
                        <GridRow>
                            <Grid item sm={12}>
                                <Box display="flex" alignItems="center">
                                    <div className="pictureContainer form-group">
                                        <AvatarImage
                                            size={60}
                                            id={state.id}
                                            name={state.code}
                                            image={state.resetImage ? state.defaultImage : state.image}
                                            editable={true}
                                            tooltip={Translations.CkickToChangeThePicture}
                                            removeTooltip={Translations.RemovePicture}
                                            onChange={this.handleSetImage}
                                            onClear={state.resetImage ? null : this.handleRemoveImage}
                                            onError={AppUtils.showErrorMessage}
                                        />
                                    </div>
                                    <Box sx={{ marginTop: "-46px", paddingLeft: "20px", width: "100%" }}>
                                        <FormField title={Translations.Code} required>
                                            <TextField name="code" title={Translations.Code} inputProps={{ maxLength: 50 }} value={state.code} required={true} onChange={this.handleChange} size="small" fullWidth />
                                        </FormField>
                                    </Box>
                                </Box>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Name} fullWidth margin="0">
                                    <TextField name="name" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.name} required={true} onChange={this.handleChange} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </GridRow>
                        {props.sessionState.isSuperAdmin &&
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.MessageTypeClass} fullWidth>
                                        <IdTitleDropdown
                                            selectedId={state.messageTypeClass.toString(10)}
                                            items={[{ id: "0", title: Translations.NormalMessage }, { id: "1", title: Translations.AdminMessage }]}
                                            onChange={(id: string) => this.setState({ messageTypeClass: id.toInteger() })}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!state.isNew ? state.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={state.isNew}
                            onCancel={this.handleCancel}
                            onRemove={!state.isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}